import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { saveSmartGoals } from "@/lib/api";
import { SMART_CRITERIA, SMART_EXAMPLES } from "@/data/abcContent";
import { Check, X, ChevronRight, Sparkles } from "lucide-react";

const EMPTY_GOAL = { text: "", specific: false, measurable: false, attainable: false, relevant: false, timeBound: false };

export default function SmartGoalsStep({ onDone }) {
  const [goals, setGoals] = useState([{ ...EMPTY_GOAL }, { ...EMPTY_GOAL }, { ...EMPTY_GOAL }]);
  const [submitting, setSubmitting] = useState(false);

  const update = (i, patch) => setGoals(goals.map((g, idx) => (idx === i ? { ...g, ...patch } : g)));

  const allValid = goals.every(g => g.text.trim().length > 5 && SMART_CRITERIA.every(c => g[c.key]));

  const handleSubmit = async () => {
    if (!allValid) {
      toast.error("Cada objetivo debe cumplir los 5 criterios SMART y tener texto.");
      return;
    }
    setSubmitting(true);
    try {
      await saveSmartGoals(goals);
      toast.success("Objetivos guardados");
      onDone(goals);
    } catch {
      toast.error("No pudimos guardar los objetivos.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl sm:text-3xl font-bold text-[var(--ha-text)]">Tus 3 objetivos SMART</h2>
      <p className="mt-3 text-[var(--ha-muted)] leading-relaxed max-w-2xl">
        Redacta tres objetivos personales de forma <strong>positiva</strong>. Deben ser específicos, medibles, alcanzables, relevantes y definidos en el tiempo.
      </p>

      {/* Examples */}
      <div className="mt-8 grid md:grid-cols-2 gap-3">
        {SMART_EXAMPLES.map((ex, i) => (
          <Card key={i} className={`rounded-2xl border ${ex.smart ? "border-[var(--ha-success)]/40 bg-[var(--ha-success)]/5" : "border-[var(--ha-error)]/30 bg-[var(--ha-error)]/5"}`}>
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                {ex.smart ? <Check className="w-5 h-5 text-[var(--ha-success)] mt-0.5" strokeWidth={2} /> : <X className="w-5 h-5 text-[var(--ha-error)] mt-0.5" strokeWidth={2} />}
                <div>
                  <p className="font-semibold text-[var(--ha-text)]">{ex.goal}</p>
                  <p className="text-xs text-[var(--ha-muted)] mt-1">{ex.smart ? "Cumple SMART" : "No cumple SMART"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-10 space-y-5">
        {goals.map((g, i) => (
          <Card key={i} className="rounded-2xl border-[var(--ha-border)] bg-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[var(--ha-secondary)]" strokeWidth={1.75} />
                <p className="text-xs uppercase tracking-[0.16em] text-[var(--ha-muted)] font-semibold">Objetivo {i + 1}</p>
              </div>
              <Input
                data-testid={`smart-goal-input-${i}`}
                placeholder="Ej: Aumentar mi actividad física a 30 min diarios durante las próximas 8 semanas."
                value={g.text}
                onChange={(e) => update(i, { text: e.target.value })}
                className="h-12 rounded-xl border-[var(--ha-border)] focus:border-[var(--ha-primary)] focus:ring-[var(--ha-primary)]"
              />
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                {SMART_CRITERIA.map(c => (
                  <label
                    key={c.key}
                    className={`cursor-pointer rounded-xl border px-3 py-2.5 flex items-center gap-2 transition-colors ${g[c.key] ? "border-[var(--ha-primary)] bg-[var(--ha-primary)]/5" : "border-[var(--ha-border)] hover:bg-[var(--ha-bg)]"}`}
                  >
                    <Checkbox
                      data-testid={`smart-goal-${i}-${c.key}`}
                      checked={g[c.key]}
                      onCheckedChange={(v) => update(i, { [c.key]: !!v })}
                    />
                    <span className="text-sm text-[var(--ha-text)]">{c.label}</span>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-end">
        <Button
          data-testid="submit-smart-goals-btn"
          onClick={handleSubmit}
          disabled={submitting || !allValid}
          className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6"
        >
          Guardar objetivos <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}
